import Flickity from 'flickity'
import { getSizedImageUrl } from '@shopify/theme-images'
import { productPrice, productPointsValue } from '../helpers/product'

import axios from 'axios'

import {
  defineStore
} from 'pinia'

export const useProductStore = defineStore('ProductStore', {
  state: () => {
    return {
      product: {},
      combinedListingsProductVariants: [],
      combinedListingsProductVariantsMetafields: {},
      lastProduct: {},
      selectedVariant: {},
      selectedSellingPlan: null,
      backInStockPopupIsShowing: false,
    }
  },

  actions: {
    changeVariant (variant) {
      this.selectedVariant = variant
    },

    changeSellingPlan (sellingPlan) {
      this.selectedSellingPlan = sellingPlan
    },

    setInitialVariant () {
      let defaultVariant = this.product.variants[0]
      if (this.product.available) {
        defaultVariant = this.product.variants.find(variant => variant.available)
      }
      this.selectedVariant = defaultVariant
    },

    setInitialProduct () {
      this.product = {
        ...window.theme.product,
        metafields: { ...window.theme.productMetafields },
        inventoryQuantity: window.theme.productInventoryQuantity
      }

      if (window.theme.combinedListingsProductVariants) {
        this.product.variants = window.theme.combinedListingsProductVariants.map(variantProduct => {
          const variantData = { ...variantProduct.variants[0] }
          variantData.featured_image = variantProduct.featured_image
          return variantData
        })

        this.combinedListingsProductVariants = window.theme.combinedListingsProductVariants
        this.combinedListingsProductVariantsMetafields = window.theme.combinedListingsProductVariantsMetafields
      }

      this.lastProduct = this.product

      this.updateYotpo()
    },

    async updateYotpo () {
      const reviewsWidget = document.querySelector('.shopify-app-block .yotpo-widget-instance')
      const starsWidgets = [document.querySelector('.product__price-line [data-yotpo-instance-id]'), document.querySelector('.product__accordion-item-title [data-yotpo-instance-id]')]
      const productId = this.isCombinedListingsProduct ? this.combinedListingsProduct.id : this.product.id
      if (reviewsWidget) {
        reviewsWidget.setAttribute('data-yotpo-product-id', productId)
      }

      if (starsWidgets) {
        starsWidgets.forEach(widget => {
          if (widget) {
            const instanceId = widget.getAttribute('data-yotpo-instance-id')
            widget.innerHTML = `<div class="yotpo-widget-instance" data-yotpo-instance-id="${instanceId}" data-yotpo-product-id="${productId}" data-yotpo-section-id="product"></div>`
          }
        })
      }

      const { yotpoWidgetsContainer } = window
      if (yotpoWidgetsContainer) {
        await setTimeout(async () => { await yotpoWidgetsContainer.initWidgets() }, 300)
      }
    },

    async changeSelectedVariant (variantId) {
      const variant = this.product.variants.find(variant => variant.id === variantId)
      this.selectedVariant = variant
      // update URL
      // const newUrl = `/products/${this.product.handle}?variant=${variantId}`
      // history.replaceState({}, null, newUrl)    

      // update Yotpo
      await this.updateYotpo()
    },

    async changeProductByHandle ({ handle, metafields = null, inventoryQuantity = 99999 }) {
      document.body.classList.add('switching-product')

      const endpoint = `${window.Shopify.routes.root}products/${handle}.js`
      await axios
        .get(endpoint)
        .then(async response => {
          // update last product
          this.lastProduct = this.product

          // update product
          this.product = {
            ...response.data,
            metafields,
            inventoryQuantity
          }

          // update selectedVariant
          this.setInitialVariant()

          // update URL
          const newUrl = `/products/${response.data.handle}`
          history.replaceState({}, null, newUrl)

          // update Yotpo
          await this.updateYotpo()

          setTimeout(() => { document.body.classList.remove('switching-product') }, 300)
        })
        .catch(error => {
          console.log(error)

          setTimeout(() => { document.body.classList.remove('switching-product') }, 300)
        })
    },

    showBackInStockPopup () {
      this.backInStockPopupIsShowing = true
      document.body.classList.add('overflow-hidden')
    },

    hideBackInStockPopup (event = null) {
      if (!event || event.target.hasAttribute('data-close'))
        this.backInStockPopupIsShowing = false
        document.body.classList.remove('overflow-hidden')
    }
  },

  getters: {
    // productType (state) {
    //   let productType = ''
    //   state.product.tags.forEach(tag => {
    //     if (tag.includes('flavour:')) {
    //       productType = 'flavour'
    //     }
    //   })
    //   return productType
    // },

    isCombinedListingsProduct () {
      return this.product.tags?.includes('combined')
    },

    combinedListingsProduct () {
      if (!this.isCombinedListingsProduct) return null

      const product = this.combinedListingsProductVariants.find(product => product.variants.find(variant => variant.id === this.selectedVariant.id))
      const metafieldsProduct = this.combinedListingsProductVariantsMetafields.find(variant => variant.id === this.selectedVariant.id)
      
      return {
        ...product,
        metafields: {...metafieldsProduct?.productMetafields}
      }
    },

    productHasSubscriptionOption () {
      return this.product.selling_plan_groups && this.product.selling_plan_groups.length > 0
    },

    productHasPurchaseLimit () {
      return !!this.product.tags.find(tag => tag.includes('limit-'))
    },

    purchaseLimit () {
      return this.productHasPurchaseLimit ? parseInt(this.product.tags.find(tag => tag.includes('limit-')).replace('limit-', '')) : 999999
    },

    flavourCollectionHandle (state) {
      const servingTypes = window.theme.productTags.servingTypeTagKeys.split(',')
      let isValidServingType = false

      servingTypes.forEach(servingType => {
        const tag = window.theme.productTags[`${servingType}Tag`]
        if (state.product.tags.includes(tag)) {
          isValidServingType = true
        }
      })

      if (isValidServingType) {
        const flavourTag = state.product.tags.find(tag => tag.includes('flavour:'))
        if (flavourTag) {
          const flavourCollectionHandle = flavourTag.replace('flavour:', '')
          return flavourCollectionHandle
        }
      }

      return null
    },

    defaultVariant (state) {
      let defaultVariant = state.product.variants[0]
      if (state.product.available) {
        defaultVariant = state.product.variants.find(variant => variant.available)
      }
      return defaultVariant
    },

    shortDescription (state) {
      return state.product.metafields.shortDescription
    },

    priceFormatted (state) {
      if (state.selectedSellingPlan) {
        return state.selectedSellingPlan.price
      }
      return productPrice(state.product.price)
    },

    saleBadge (state) {
      let saleBadge = null

      state.product.tags.forEach(tag => {
        if (tag.includes('promo-badge:')) {
          saleBadge = tag.split('promo-badge:')[1]
          return
        }
      })
      return saleBadge
    },

    badges (state) {
      let badges = []

      state.product.tags.forEach(tag => {
        if (tag.includes('badge-')) badges.push(tag.split('badge-')[1])
      })
      return badges.length > 0 ? badges : null
    },

    gradientBackgroundColours (state) {
      return state.product.metafields ? state.product.metafields.gradientBackgroundColours : null
    },

    hasColouredBgOption (state) {
      // let hasColouredBgOption = false
      // if (state.product.images[0].includes('.jpg')) {
      //   const colouredBgProductTagKeys = window.theme.productTags.colouredBgProductTagKeys.split(',')
      //   colouredBgProductTagKeys.forEach(tagKey => {
      //     const tag = window.theme.productTags[`${tagKey}Tag`]
      //     if (state.product.tags.includes(tag)) {
      //       hasColouredBgOption = true
      //       return
      //     }
      //   })
      // }
      //
      // return hasColouredBgOption

      return state.product.images[0].includes('.jpg') && state.product.images[1].includes('.png')
    },

    variantCollectionTriggerImage () {
      return getSizedImageUrl(this.galleryMedia[0].preview_image.src, '48x')
    },

    variantTriggerImage () {
      return getSizedImageUrl(this.selectedVariant.featured_image, '48x')
    },

    galleryMedia (state) {
      return state.product.media.filter((image, index) => {
        return !(this.hasColouredBgOption && index === 0)
      })
    },

    thumbnailImages () {
      return this.galleryMedia.map(image => {
        return getSizedImageUrl(image.preview_image.src, '96x')
      })
    },

    largeMedia () {
      return this.galleryMedia.map(image => {
        return {
          ...image,
          preview_image: {
            ...image.preview_image,
            src: getSizedImageUrl(image.preview_image.src, '1000x')
          }
        }
      })
    },
  },
})
